import type { ProductState } from '@aviata/products-fns';
import type {
  Passenger,
  PassengerKey,
  PassengersData,
} from '@aviata/booking-data';
import { isObject, omit } from 'lodash-es';
import type { Airflow } from '@aviata/platform-types';
import { citizenshipEntity } from 'shared';

import type { NormalizedMIProduct } from '@/widgets/medical-insurance/types';
import { passengersFormLib } from '@/widgets/passengers-form';

export const mapMedicalInsurance = (
  raw: Airflow.Products.MedicalInsurance,
): NormalizedMIProduct => {
  const { passenger_idx, ...rest } = raw;

  return {
    passengerIdx: passenger_idx,
    ...rest,
  };
};

const formatDate = (dateWithDots: string) => dateWithDots.split('.').join('-');

function formatPassengerDOBs(
  passenger: Passenger,
  isFunSunProvider?: boolean,
  airlineBonusCardAirlines?: string[],
) {
  const isCountryWithoutSeries =
    citizenshipEntity.config.countriesWithoutSeries.includes(
      passenger.citizenship,
    );

  const formattedPassenger: Record<string, unknown> = {
    ...omit(passenger, 'fin'),
    dob: formatDate(passenger.dob),
    document: {
      ...omit(passenger.document, ['givenAt', 'series', 'organization']),
      expiresAt: formatDate(passenger.document.expiresAt),
    },
  };

  if (isFunSunProvider) {
    if (passenger.citizenship === citizenshipEntity.config.AZERBAIJAN_CODE) {
      formattedPassenger.fin = passenger.fin;
    }
    const doc = formattedPassenger.document as Record<string, unknown>;
    if (!isCountryWithoutSeries) {
      doc.series = passenger.document.series;
    }
    doc.givenAt = passenger.document.givenAt
      ? formatDate(passenger.document.givenAt)
      : undefined;
    doc.organization = passenger.document.organization;
  }

  const milesCardNumber =
    passengersFormLib.milesCardsStorageLib.getMilesCardNumberByPassengerId(
      passenger.id,
    );

  if (
    airlineBonusCardAirlines &&
    airlineBonusCardAirlines?.length > 0 &&
    milesCardNumber
  ) {
    formattedPassenger.airlineBonusCard = milesCardNumber;
  }

  return formattedPassenger;
}

export function getProductsPriceInfo(state: ProductState) {
  if (state.id && !state.selectedAsPackage) return state.price;
  return 0;
}

export function mapPassengers(
  passengers: PassengersData,
  isFunSunProvider?: boolean,
  airlineBonusCardAirlines?: string[],
) {
  const keys = Object.keys(passengers) as PassengerKey[];
  return keys.reduce(
    (acc, key) => {
      const pax = passengers[key];

      if (isObject(pax)) {
        acc[key] = formatPassengerDOBs(
          pax,
          isFunSunProvider,
          airlineBonusCardAirlines,
        );
      }

      return acc;
    },
    {} as Record<string, unknown>,
  );
}
